import React, { useState, useMemo } from 'react';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as faIcons from '@fortawesome/free-solid-svg-icons';
import CgCnGoodHoverText from 'modules/Common/entities/CgCnGoodHoverText';
import CgTinyBlockLoader from 'modules/Common/ui/CgTinyBlockLoader';
import { useFetchCnGoodsCbam } from 'modules/Common/hooks/useFetchCnGoodsCbam';
import { createCnGoodFilterOption } from 'modules/Common/utils/cnGoodFilterOption';
import { useTranslation } from 'react-i18next';
import { DANGER } from 'modules/Common/config/palette';
import { useCgDialogPortalContainer } from 'modules/Common/ui/CgDialog';
import { withPortalOffset } from 'modules/Common/utils/overlayHost';

/**
 * CgCnGoodSelector - Dropdown component for selecting CN Goods
 * Features:
 * - Searchable dropdown with CN Good hover text components
 * - Similar to CgCnGoodsList but for single selection
 * - Returns selected CN Good with full data
 * - Optional filtering by available CN goods IDs
 */
export default function CgCnGoodSelector({
  value,
  onChange,
  placeholder = null,
  disabled = false,
  error = null,
  className = '',
  filterByCnGoodIds = null, // Array of CN good IDs to filter by, or null to show all
  hideLabel = false,
  controlMinHeight = 48,
  showSelectedSubtitle = true, // Whether to show the "Selected: CN Code - Name" section below dropdown
  // When true, the selected value displayed inside the control reads
  // "CN Code - Name" instead of just the CN code pill. Only affects the
  // selected-value display; dropdown OPTIONS keep their existing layout.
  showCnName = false,
}) {
  const { t } = useTranslation('quotes');
  const dialogContainer = useCgDialogPortalContainer();
  const portalTarget = dialogContainer ?? (typeof document !== 'undefined' ? document.body : null);
  // Fetch CN goods
  const { data: cnGoodsData = [], isLoading } = useFetchCnGoodsCbam();

  // Create options for the dropdown
  const cnGoodOptions = useMemo(() => {
    let filteredGoods = cnGoodsData;

    // Apply filter if provided (null = no filter, empty array = no results, array with items = filtered results)
    if (filterByCnGoodIds && Array.isArray(filterByCnGoodIds)) {
      filteredGoods = cnGoodsData.filter((good) => filterByCnGoodIds.includes(good._id));
    }

    return filteredGoods.map((good) => ({
      value: good._id,
      // Localized category label (additive, non-English responses) — the
      // only translated display field until CN names localize server-side.
      label: [
        good._id,
        good.name,
        good.aggregatedGoodsCategoryLabel ?? good.aggregatedGoodsCategory,
      ]
        .filter(Boolean)
        .join(' - '),
      cnGood: good,
    }));
  }, [cnGoodsData, filterByCnGoodIds]);

  // Find current selected option
  const selectedOption = useMemo(() => {
    if (!value) return null;

    const found = cnGoodOptions.find((option) => option.value === value);
    return found || null;
  }, [value, cnGoodOptions]);

  const handleChange = (selectedOption) => {
    if (!selectedOption) {
      onChange('', '');
      return;
    }

    const cnGood = selectedOption.cnGood;
    onChange(cnGood._id, cnGood.name, cnGood);
  };

  // Custom option component with improved styling
  const OptionComponent = ({ data, ...props }) => {
    const cnGood = data.cnGood;
    if (!cnGood) return null;

    return (
      <div
        {...props.innerProps}
        className={`px-3 py-2 hover:bg-gray-50 cursor-pointer min-h-[50px] flex items-center ${
          props.isFocused ? 'bg-gray-50' : ''
        } ${props.isSelected ? 'bg-blue-50' : ''}`}
      >
        <div className="flex-1 min-w-0">
          <CgCnGoodHoverText
            cnGood={cnGood}
            position="bottom"
          />
          <div
            className="text-xs text-gray-600 truncate mt-1"
            title={cnGood.name}
            style={{ maxWidth: '450px' }}
          >
            {cnGood.name}
          </div>
        </div>
      </div>
    );
  };

  // Custom single value component with hover text. By default only the CN
  // code pill is shown; with `showCnName=true` the good's name renders to
  // the RIGHT of the pill and wraps within its own column only when it
  // overflows. The pill stays anchored at the start of the row regardless
  // of how many lines the name needs. The control grows vertically to fit
  // (see `height: auto` in styles below).
  const SingleValueComponent = ({ data, ...props }) => {
    return (
      <div
        {...props.innerProps}
        className={`flex gap-2 min-w-0 ${
          // `items-baseline` aligns the pill's text baseline with the
          // name's first-line baseline — keeps the pill visually flush with
          // the name when the row is one line, and anchored to the top
          // line when the name wraps.
          showCnName ? 'items-baseline py-1' : 'items-center'
        }`}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="shrink-0"
        >
          <CgCnGoodHoverText
            cnGood={data.cnGood}
            position="bottom"
          />
        </div>
        {showCnName && data.cnGood?.name ? (
          // `flex-1 min-w-0` lets the span shrink so `break-words` /
          // `whitespace-normal` can wrap. No `flex-wrap` on the parent —
          // the span wraps internally and never falls below the pill.
          <span
            className="text-sm text-gray-700 whitespace-normal break-words leading-snug flex-1 min-w-0"
            title={data.cnGood.name}
          >
            {data.cnGood.name}
          </span>
        ) : null}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 bg-white rounded-lg border border-gray-200">
        <CgTinyBlockLoader />
        <span className="text-sm text-gray-500">
          {t('items.loadingCnGoods', 'Loading CN goods...')}
        </span>
      </div>
    );
  }

  return (
    <div className={className}>
      <Select
        options={cnGoodOptions}
        value={selectedOption}
        onChange={handleChange}
        placeholder={placeholder ?? t('fields.selectCnCode', 'Select CN Code')}
        isSearchable
        isClearable
        isDisabled={disabled}
        // Portal into the host CgDialog's content when there is one, and to the
        // body otherwise. The body is right on a plain page — it escapes
        // overflow:hidden ancestors and outranks other overlays — but wrong
        // inside a dialog: Radix traps focus within Dialog.Content, so a menu
        // parked on <body> is focus-outside. The first pointerdown on an option
        // pulls focus back to the dialog, react-select blurs and closes the
        // menu, and the option is gone before its click lands — the field could
        // not be set with a mouse at all. Same reasoning (and the same
        // `dialogContainer ?? body` shape) as SearchSelectField.
        menuPortalTarget={portalTarget}
        components={{
          Option: OptionComponent,
          SingleValue: SingleValueComponent,
        }}
        styles={{
          control: (base, state) => ({
            ...base,
            borderColor: error ? DANGER : state.isFocused ? '#00b894' : '#d1d5db',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(0, 184, 148, 0.2)' : 'none',
            '&:hover': {
              borderColor: error ? DANGER : '#00b894',
            },
            minHeight: `${controlMinHeight}px`,
            // Lock the height for the default (pill-only) layout so it
            // visually matches the surrounding inputs. When `showCnName` is
            // on, the control needs to grow vertically to fit a wrapped
            // name, so we drop the fixed height + overflow-hidden.
            ...(showCnName
              ? { height: 'auto' }
              : { height: `${controlMinHeight}px`, overflow: 'hidden' }),
            borderRadius: '5px',
            fontSize: '14px',
          }),
          placeholder: (base) => ({
            ...base,
            color: '#6b7280',
          }),
          // Keep the menu above the Joyride overlay (z-index 10060 in
          // DiagramEditor). Without this, opening the dropdown while a guide is
          // running hides the options behind the spotlight overlay.
          menu: (base) => ({
            ...base,
            zIndex: 20001,
            minWidth: '250px',
          }),
          // react-select positions a portaled menu from the control's viewport
          // rect; inside a dialog that lands offset by the dialog's own corner.
          // See withPortalOffset.
          menuPortal: (base) => ({
            ...withPortalOffset(base, portalTarget),
            zIndex: 20001,
          }),
          option: (base, state) => ({
            ...base,
            padding: 0, // We handle padding in our custom component
            minHeight: '50px', // Give more space for two lines
            backgroundColor: state.isFocused ? '#f9fafb' : base.backgroundColor,
            ':hover': {
              backgroundColor: '#f3f4f6',
            },
          }),
          singleValue: (base) => ({
            ...base,
            display: 'flex',
            alignItems: showCnName ? 'baseline' : 'center',
            width: '100%',
            // react-select's default SingleValue uses white-space: nowrap +
            // overflow: hidden + text-overflow: ellipsis to keep its
            // content on a single line. We need the wrapped-name layout to
            // grow, so we override only when `showCnName` is on.
            ...(showCnName
              ? { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip' }
              : {}),
          }),
          valueContainer: (base) => ({
            ...base,
            display: 'flex',
            alignItems: showCnName ? 'baseline' : 'center',
          }),
        }}
        filterOption={createCnGoodFilterOption}
        noOptionsMessage={({ inputValue }) =>
          inputValue
            ? t('items.noGoodsMatching', 'No CN goods found matching "{{query}}"', {
                query: inputValue,
              })
            : t('items.noGoodsAvailable', 'No CN goods available')
        }
        loadingMessage={() => t('items.loadingCnGoods', 'Loading CN goods...')}
      />

      {error && <div className="text-red-600 text-sm mt-1">{error}</div>}

      {showSelectedSubtitle && value && selectedOption && (
        <div className="mt-2 text-xs text-gray-600">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon
              icon={faIcons.faBoxes}
              className="text-primaryColor"
            />
            <span>
              {t('state.selectedLabel', 'Selected:')}{' '}
              <span className="font-medium">{selectedOption.cnGood._id}</span>
            </span>
            <span className="text-gray-500">•</span>
            <span
              className="truncate"
              title={selectedOption.cnGood.name}
            >
              {selectedOption.cnGood.name}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
